import { useEffect, useMemo, useState } from 'react';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender, type ColumnDef, type SortingState } from '@tanstack/react-table';
import { api, type JournalHistoryRow } from '../shared/api-client';
import './JournalHistoryComponent.css';

/**
 * History: what the journal recorded for one thing, in order (plans/2026-10-24-chat-v1-
 * hardening.md, phase 7 step 5): a chat turn opening and ending, every workflow and
 * activity attempt, each tool call and outbound call, under one correlation.
 */
export function JournalHistoryComponent({ about }: { about: { correlationId?: string; threadId?: string; incidentId?: string } }): JSX.Element {
  const [rows, setRows] = useState<JournalHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const { correlationId, threadId, incidentId } = about;

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    void api.getJournalHistory({ correlationId, threadId, incidentId }).then((result) => {
      if (cancelled) return;
      if (result.success) setRows(result.data.rows);
      else setError(result.error.message);
    });
    return () => { cancelled = true; };
  }, [correlationId, threadId, incidentId]);

  const columns = useMemo<ColumnDef<JournalHistoryRow>[]>(() => [
    { id: 'at', header: 'When', accessorFn: (r) => r.at, cell: ({ row }) => new Date(row.original.at).toLocaleTimeString(undefined, { hour12: false, fractionalSecondDigits: 3 }) },
    { id: 'kind', header: 'What', accessorFn: (r) => r.kind },
    { id: 'detail', header: 'Detail', enableSorting: false, cell: ({ row }) => journalDetail(row.original) },
    { id: 'correlation', header: 'Correlation', accessorFn: (r) => r.correlationId ?? '', cell: ({ row }) => <code>{row.original.correlationId ?? '—'}</code> },
  ], []);
  const table = useReactTable({ data: rows ?? [], columns, state: { sorting }, onSortingChange: setSorting, getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel() });

  if (error) return <div className="alert alert-error">{error}</div>;
  if (!rows) return <p className="journal-history-empty">Loading history…</p>;
  if (rows.length === 0) return <p className="journal-history-empty">Nothing journaled yet.</p>;
  return (
    <div className="journal-history">
      <table className="journal-history-table">
        <thead>
          {table.getHeaderGroups().map((g) => (
            <tr key={g.id}>
              {g.headers.map((h) => (
                <th key={h.id} onClick={h.column.getToggleSortingHandler()} className={h.column.getCanSort() ? 'sortable' : undefined}>
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((r) => (
            <tr key={r.id} data-kind={r.original.kind}>
              {r.getVisibleCells().map((c) => <td key={c.id}>{flexRender(c.column.columnDef.cell, c.getContext())}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A row's payload, in a few words. */
function journalDetail(row: JournalHistoryRow): string {
  const p = (row.payload ?? {}) as Record<string, unknown>;
  const s = (k: string): string => (typeof p[k] === 'string' || typeof p[k] === 'number' ? String(p[k]) : '');
  switch (row.kind) {
    case 'activity.attempt': return `${s('activityType')} #${s('attempt')} ${s('outcome')} in ${s('durationMs')} ms${p.error ? `: ${s('error')}` : ''}`;
    case 'chat.tool.called': return `${s('toolName')} ${s('status')} in ${s('durationMs')} ms${p.error ? `: ${s('error')}` : ''}`;
    case 'outbound.call': return `${s('method')} ${s('url')} → ${p.status !== undefined ? s('status') : s('error')} in ${s('durationMs')} ms`;
    case 'chat.turn.ended': return `${s('outcome')} (by ${s('closedBy')})${p.error ? `: ${s('error')}` : ''}`;
    case 'chat.prompt.opened':
    case 'chat.prompt.answered': return `${s('toolName')} (${s('kind')})${row.actor ? ` by ${row.actor}` : ''}`;
    case 'slack.reply.posted': return `${s('channel')} ${s('ts')}`;
    default: return s('workflowType') || s('caller') || '';
  }
}
